/**
 * Client error sink. Posts to our own server, never to a third party — that
 * would defeat the point of the project (ADR-0002). See docs/debugging.md.
 *
 * Batched, capped and rate-limited, because the failure mode we care about is
 * an error loop on a phone with a metered connection.
 */

type Entry = {
  ts: number;
  level: 'warn' | 'error';
  kind: string;
  message: string;
  context?: unknown;
  reqId?: string;
};

const queue: Entry[] = [];
const MAX_QUEUE = 50;
const FLUSH_MS = 10_000;
let sentThisSession = 0;
const MAX_PER_SESSION = 200;

export function report(entry: Omit<Entry, 'ts'>): void {
  if (sentThisSession >= MAX_PER_SESSION) return;
  if (queue.length >= MAX_QUEUE) queue.shift(); // drop oldest, keep recent
  queue.push({ ...entry, ts: Date.now() });
}

async function flush(useBeacon = false): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  sentThisSession += batch.length;
  const body = JSON.stringify({ entries: batch });

  try {
    // On page hide, fetch is cancelled; sendBeacon survives.
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon('/api/telemetry', new Blob([body], { type: 'application/json' }));
      return;
    }
    await fetch('/api/telemetry', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Telemetry must never break the app or retry forever. Drop it.
  }
}

export function installTelemetry(): void {
  window.addEventListener('error', (e) => {
    report({
      level: 'error',
      kind: 'window.error',
      message: e.message,
      context: { src: e.filename, line: e.lineno, col: e.colno },
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    report({
      level: 'error',
      kind: 'unhandledrejection',
      message: String((e.reason as Error)?.message ?? e.reason),
    });
  });

  setInterval(() => void flush(), FLUSH_MS);
  // Not 'unload': it does not fire reliably on mobile Chrome.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
}
